/**
 * Message body selection.
 *
 * List endpoints return only a short `text` preview (flagged `isPreview`) plus
 * `hasHtml` when an html part exists but was not included in that payload. Rendering
 * the preview would flash unstyled plain text over the real message for as long as
 * the detail request takes, so `getRenderableBody` reports `isLoading` instead and
 * lets the caller show a placeholder.
 *
 * After the detail response arrives, `isPreview` must be cleared. Bounce / DSN /
 * undelivered notices often have usable `text` with empty or bogus `html` and a
 * stale `hasHtml` flag — those must fall through to text, not stay in loading.
 */

export type EmailBody = {
  html?: string | null;
  text?: string | null;
  raw?: string | null;
  /** true when `text` is a truncated list preview rather than the full body */
  isPreview?: boolean;
  /** true when an html part exists, even if it was not sent in this payload */
  hasHtml?: boolean;
};

export type EmailBodyLike = EmailBody | null | undefined;

export interface RenderableBody {
  /** html safe to inject; empty when there is nothing to show yet */
  html: string;
  /** the real body has not arrived yet — show a placeholder, never the preview */
  isLoading: boolean;
  /** the message genuinely has no content */
  isEmpty: boolean;
}

/** Treat API quirks like literal "0" / 0 / empty as missing content. */
export const isUsableBodyPart = (value: unknown): value is string => {
  if (typeof value === 'number') return false;
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (trimmed === '0') return false;
  return true;
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const QUOTE_PREFIX_RE = /^\s*>+\s?/;
const HTML_MARKUP_RE = /<!doctype\s+html|<html[\s>]|<head[\s>]|<body[\s>]|<(?:div|table|style|p|center|td|tr|span|br)\b/i;

const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

const wrapEscapedText = (text: string): string => {
  if (!text.trim()) return '';

  const linked = escapeHtml(text).replace(
    /\b(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  return `<div style="white-space:pre-wrap;word-break:break-word;">${linked}</div>`;
};

/** Plain-text replies often quote the original HTML source line by line (`> <html>`). */
const isQuotedHtmlSource = (text: string): boolean => {
  let markupLines = 0;

  for (const line of text.split(/\r?\n/)) {
    if (!QUOTE_PREFIX_RE.test(line)) continue;
    if (HTML_MARKUP_RE.test(line.replace(QUOTE_PREFIX_RE, ''))) markupLines += 1;
    if (markupLines >= 2) return true;
  }

  return false;
};

const quotedBlockEnd = (lines: string[], start: number): number => {
  let end = start;

  while (end < lines.length) {
    const line = lines[end];
    if (QUOTE_PREFIX_RE.test(line)) {
      end += 1;
      continue;
    }

    if (line.trim() === '') {
      let look = end + 1;
      while (look < lines.length && lines[look].trim() === '') look += 1;
      if (look < lines.length && QUOTE_PREFIX_RE.test(lines[look])) {
        end += 1;
        continue;
      }
    }

    break;
  }

  return end;
};

const extractHtmlFragment = (html: string): string => {
  const withoutScripts = html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
  const styles = Array.from(withoutScripts.matchAll(/<style\b[^>]*>[\s\S]*?<\/style>/gi))
    .map((match) => match[0].replace(/\bbody\b/gi, '.quoted-mail'))
    .join('');
  const body = withoutScripts.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
  const inner = body
    ? body[1]
    : withoutScripts
        .replace(/<!doctype[^>]*>/gi, '')
        .replace(/<\/?html\b[^>]*>/gi, '')
        .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, '');

  return `${styles}<div class="quoted-mail">${inner}</div>`;
};

const renderQuotedHtmlText = (text: string): string => {
  const lines = text.split(/\r?\n/);
  const start = lines.findIndex(
    (line) => QUOTE_PREFIX_RE.test(line) && HTML_MARKUP_RE.test(line.replace(QUOTE_PREFIX_RE, ''))
  );

  if (start === -1) return wrapEscapedText(text);

  const end = quotedBlockEnd(lines, start);
  const quotedHtml = lines
    .slice(start, end)
    .map((line) => line.replace(QUOTE_PREFIX_RE, ''))
    .join('\n');

  return [
    wrapEscapedText(lines.slice(0, start).join('\n')),
    `<blockquote class="quoted-mail" style="margin:12px 0 0;border-left:3px solid #dadce0;padding-left:12px;">${extractHtmlFragment(quotedHtml)}</blockquote>`,
    wrapEscapedText(lines.slice(end).join('\n')),
  ].join('');
};

/**
 * Turn a plain-text body into HTML. Quoted HTML documents inside replies are
 * rendered, not shown as escaped source.
 */
export const textToHtml = (text: string): string => {
  if (!text) return '';
  if (isQuotedHtmlSource(text)) return renderQuotedHtmlText(text);
  return wrapEscapedText(text);
};

const unwrapTextDump = (html: string): string | null => {
  const match = html.trim().match(
    /^<div\s+style="white-space:\s*pre-wrap[^"]*">([\s\S]*)<\/div>$/i
  );
  if (!match) return null;
  return decodeHtmlEntities(match[1]);
};

/** Repair API html that is just an escaped plain-text dump of a quoted HTML mail. */
export const prepareDisplayHtml = (html: string): string => {
  const unwrapped = unwrapTextDump(html);
  if (unwrapped && isQuotedHtmlSource(unwrapped)) return renderQuotedHtmlText(unwrapped);
  if (isQuotedHtmlSource(html)) return renderQuotedHtmlText(html);
  return html;
};

/**
 * Body content to display, or '' when the real body is still loading. A list preview
 * is deliberately not returned — see the note at the top of this file.
 */
export const pickBodyContent = (body: EmailBodyLike): string => {
  if (!body || typeof body !== 'object') return '';
  if (isUsableBodyPart(body.html)) return prepareDisplayHtml(body.html);

  // Only suppress text while we are still waiting on a detail payload that should
  // include HTML. Stale hasHtml with no html (common on bounce/DSN) must not block text.
  if (body.isPreview && body.hasHtml) return '';

  if (!body.isPreview && isUsableBodyPart(body.text)) return textToHtml(body.text);
  if (isUsableBodyPart(body.raw)) return body.raw;
  return '';
};

export const hasUsableBody = (body: EmailBodyLike): boolean => {
  return pickBodyContent(body).length > 0;
};

export const getRenderableBody = (body: EmailBodyLike): RenderableBody => {
  if (!body || typeof body !== 'object') {
    return { html: '', isLoading: false, isEmpty: true };
  }

  if (isUsableBodyPart(body.html)) {
    return { html: prepareDisplayHtml(body.html), isLoading: false, isEmpty: false };
  }

  // List row only: html exists on the server but was not included yet
  if (body.isPreview && body.hasHtml) {
    return { html: '', isLoading: true, isEmpty: false };
  }

  // Full text body (bounce/DSN reports, plain-text mail, etc.)
  if (!body.isPreview && isUsableBodyPart(body.text)) {
    return { html: textToHtml(body.text), isLoading: false, isEmpty: false };
  }

  if (isUsableBodyPart(body.raw)) {
    return { html: body.raw, isLoading: false, isEmpty: false };
  }

  // Preview-only list payload with no html promised — still wait for detail
  if (body.isPreview) {
    return { html: '', isLoading: true, isEmpty: false };
  }

  return { html: '', isLoading: false, isEmpty: true };
};

export interface MergedEmailBody {
  html: string;
  text: string;
  raw: string;
  isPreview?: boolean;
  hasHtml?: boolean;
}

/** Prefer API body when valid; otherwise keep the previous/list body. */
export const mergeEmailBody = (
  incoming: EmailBodyLike,
  fallback: EmailBodyLike
): MergedEmailBody => {
  const shape = (body: EmailBodyLike, opts?: { clearPreview?: boolean }): MergedEmailBody => ({
    html: isUsableBodyPart(body?.html) ? body!.html! : '',
    text: isUsableBodyPart(body?.text) ? body!.text! : '',
    raw: isUsableBodyPart(body?.raw) ? body!.raw! : '',
    isPreview: opts?.clearPreview ? false : body?.isPreview,
    hasHtml: body?.hasHtml,
  });

  if (hasUsableBody(incoming)) return shape(incoming);
  if (hasUsableBody(fallback)) return shape(fallback);

  return {
    html: '',
    text: isUsableBodyPart(incoming?.text)
      ? incoming!.text!
      : isUsableBodyPart(fallback?.text)
        ? fallback!.text!
        : '',
    raw: isUsableBodyPart(incoming?.raw)
      ? incoming!.raw!
      : isUsableBodyPart(fallback?.raw)
        ? fallback!.raw!
        : '',
    isPreview: incoming?.isPreview,
    hasHtml: incoming?.hasHtml,
  };
};

/** Mark body as coming from the detail API so loading placeholders stop. */
export const finalizeDetailBody = (
  incoming: EmailBodyLike,
  fallback: EmailBodyLike
): MergedEmailBody => {
  const merged = mergeEmailBody(incoming, fallback);
  return {
    ...merged,
    // Detail fetch completed — never keep the list "still loading" flags
    isPreview: false,
    hasHtml: isUsableBodyPart(merged.html),
  };
};
